/**
 * Which tags are one tag in the singular and the plural ("template" and
 * "templates"), and which spelling a new tag is stored under.
 *
 * WHY. Tags are the app's only taxonomy and every tag is a page (/tag/{slug}).
 * On 2026-10-08 four topics were split across two spellings each, so their pages
 * stayed under the three products a tag page needs to be indexed
 * (TAG_INDEX_MIN_PRODUCTS in public-catalog.ts), and one seller tagged a listing
 * both ways to be found under either. So a new tag joins the spelling already in
 * use: the picker (src/components/tag-selector.tsx) and the importer
 * (functions/src/import-export.ts) do it as a tag is added, and
 * functions/scripts/merge-plural-tags.mjs folds pairs that already exist.
 *
 * PAIRS, NOT A STEMMER. Two tags are paired only when one is the regular English
 * plural of the other, and a new tag only ever joins a spelling that already
 * exists. Nothing strips a trailing "s" on its own: "economics", "business" and
 * "savings-goals" are tags as typed. A false pair would send sellers to the
 * wrong tag and, through the cleanup script, rename it on every product, so the
 * plural-looking words that name something else ("canvas" is not more than one
 * Canva) are in NOT_PLURALS, and the script's dry run lists every merge (its
 * --skip leaves one out).
 *
 * MIRRORED in functions/src/lib/tag-variants.ts, for the importer, the owners'
 * update notice and the cleanup script. tag-variants.test.ts runs both copies
 * over the same words and fails if they disagree.
 */

/**
 * The field the merge script (functions/scripts/merge-plural-tags.mjs) sets on
 * a merged spelling's tags/{tag} counter, naming the spelling kept. The picker
 * passes over a counter carrying it, so typing the old spelling joins the kept
 * one; the counter itself stays, because deleting it raced the counting trigger.
 */
export const MERGED_INTO_FIELD = 'mergedInto';

// Straight and curly apostrophes, and the modifier letter some keyboards type.
// Built from code points so the source holds no lookalike characters.
const APOSTROPHES = new RegExp(`[${String.fromCharCode(0x27, 0x2018, 0x2019, 0x2bc)}]`, 'g');

/**
 * Pairs that look like a singular and its plural but name different things,
 * including app and product names that end in "s" (Microsoft Word and Teams,
 * Apple Pages and Numbers, Google Sheets, Docs, Slides and Forms, The Sims,
 * 3ds Max, Nintendo 2DS). Matched on the whole tag, so
 * "canva" and "canvas" stay apart while "canva-template" and "canva-templates"
 * are still one tag.
 */
export const NOT_PLURALS: ReadonlyArray<readonly [string, string]> = [
  ['canva', 'canvas'],
  ['notion', 'notions'],
  ['new', 'news'],
  ['good', 'goods'],
  ['minute', 'minutes'],
  ['glass', 'glasses'],
  ['letter', 'letters'],
  ['word', 'words'],
  ['team', 'teams'],
  ['page', 'pages'],
  ['number', 'numbers'],
  ['sim', 'sims'],
  ['3d', '3ds'],
  ['2d', '2ds'],
  ['sheet', 'sheets'],
  ['doc', 'docs'],
  ['slide', 'slides'],
  ['form', 'forms'],
  ['electronic', 'electronics'],
];

/**
 * A typed tag in the picker's normal form: lowercased, apostrophes dropped
 * ("SVG's" is "svgs", a plural, not "svg-s"), trimmed, and each run of
 * whitespace one hyphen. Any other character is kept, as the picker always has;
 * tagSlug in catalog-tags.ts decides what reaches a URL.
 */
export function normalizeTag(raw: string): string {
  return raw.toLowerCase().replace(APOSTROPHES, '').trim().replace(/\s+/g, '-');
}

/** The regular plurals of a singular tag, read off its last letters. */
function pluralsOf(singular: string): string[] {
  // Only a tag ending in a letter has a plural: "1990s" is a decade, not more
  // than one 1990.
  if (!/[a-z]$/.test(singular)) return [];
  if (/(s|x|z|ch|sh)$/.test(singular)) return [`${singular}es`];
  if (/[^aeiou]y$/.test(singular)) return [`${singular.slice(0, -1)}ies`];
  if (singular.endsWith('o')) return [`${singular}s`, `${singular}es`];
  return [`${singular}s`];
}

/**
 * The singular behind a plural of a word ending in o: "heroes" and "heros" both
 * give "hero". pluralsOf offers both endings for such a word, so the two
 * plurals are one tag too. A stem under the picker's three characters is no
 * word: "toes" is the plural of "toe", not of "to".
 */
function oSingularOf(plural: string): string | null {
  const singular = plural.endsWith('oes') ? plural.slice(0, -2) : plural.endsWith('os') ? plural.slice(0, -1) : null;
  return singular !== null && singular.length >= 3 ? singular : null;
}

/** Whether two tags, in normal form, are one tag in the singular and the plural. */
export function isNumberVariant(a: string, b: string): boolean {
  if (a === b) return false;
  if (NOT_PLURALS.some(([x, y]) => (a === x && b === y) || (a === y && b === x))) return false;
  if (pluralsOf(a).includes(b) || pluralsOf(b).includes(a)) return true;
  const singular = oSingularOf(a);
  return singular !== null && singular === oSingularOf(b);
}

/**
 * The spelling a new tag is stored under: itself when that exact tag exists,
 * else its other number when that exists (a seller typing "template" where
 * "templates" exists gets "templates"), else itself. Of two spellings in the
 * other number, the shorter wins, then the first alphabetically.
 */
export function resolveTagSpelling(tag: string, existing: Iterable<string>): string {
  let joined: string | null = null;
  for (const name of existing) {
    if (name === tag) return tag;
    if (!isNumberVariant(tag, name)) continue;
    if (joined === null || name.length < joined.length || (name.length === joined.length && name < joined)) {
      joined = name;
    }
  }
  return joined ?? tag;
}

/**
 * A product's tags with each spelling the merge script retired replaced by the
 * spelling it was merged into (following a later merge of that one too), each
 * tag once. `mergedInto` maps a retired counter's id to its MERGED_INTO_FIELD.
 *
 * The script rewrites products and their parked edits, but a draft kept in the
 * seller's browser, or a parked edit saved while it ran, can still carry the
 * old spelling; the picker swaps it as the product is opened, so saving does
 * not bring the old tag back. A loop between two contradictory runs leaves the
 * tag as it was.
 */
export function withRetiredSpellingsReplaced(
  tags: readonly string[],
  mergedInto: ReadonlyMap<string, string>,
): string[] {
  const out: string[] = [];
  for (const tag of tags) {
    let spelling = tag;
    const seen = new Set([tag]);
    while (mergedInto.has(spelling)) {
      const next = mergedInto.get(spelling) as string;
      if (seen.has(next)) {
        spelling = tag;
        break;
      }
      seen.add(next);
      spelling = next;
    }
    if (!out.includes(spelling)) out.push(spelling);
  }
  return out;
}
